import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { debounce } from 'lodash';

interface PlacesTextFieldProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    placeholder?: string;
}

/**
 * A plain TextField with Google Places autocomplete suggestions.
 * No floating label — designed to sit under our own external label.
 */
export const PlacesTextField: React.FC<PlacesTextFieldProps> = ({
    value,
    onChange,
    onBlur,
    placeholder = 'e.g., 123 Main St, Springfield',
}) => {
    const [options, setOptions] = useState<string[]>([]);
    const [apiReady, setApiReady] = useState(false);

    useEffect(() => {
        const check = () => !!(window as any).google?.maps?.places;
        if (check()) { setApiReady(true); return; }
        const iv = setInterval(() => { if (check()) { setApiReady(true); clearInterval(iv); } }, 200);
        const to = setTimeout(() => { clearInterval(iv); setApiReady(true); }, 8000);
        return () => { clearInterval(iv); clearTimeout(to); };
    }, []);

    const fetchRef = useRef(
        debounce(async (input: string) => {
            if (!input || !(window as any).google?.maps?.places) { setOptions([]); return; }
            try {
                const svc = new (window as any).google.maps.places.AutocompleteService();
                svc.getPlacePredictions(
                    { input, types: ['address'], componentRestrictions: { country: 'US' } },
                    (predictions: any[] | null, status: string) => {
                        if (status === 'OK' && predictions) {
                            setOptions(predictions.map((p) => p.description));
                        } else {
                            setOptions([]);
                        }
                    },
                );
            } catch {
                setOptions([]);
            }
        }, 300),
    );

    const handleInputChange = useCallback(
        (_: any, newValue: string) => {
            onChange(newValue);
            if (apiReady && newValue.length >= 3) {
                fetchRef.current(newValue);
            } else {
                setOptions([]);
            }
        },
        [apiReady, onChange],
    );

    return (
        <Autocomplete
            freeSolo
            options={options}
            inputValue={value}
            onInputChange={handleInputChange}
            onChange={(_, selected) => {
                if (typeof selected === 'string') onChange(selected);
            }}
            filterOptions={(x) => x}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size="small"
                    placeholder={placeholder}
                    onBlur={onBlur}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
                />
            )}
        />
    );
};
